import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { SafariViewController } from '@awesome-cordova-plugins/safari-view-controller/ngx';
import { NavController, Platform, ToastController } from '@ionic/angular';
import { EMPTY, of, Subject } from 'rxjs';
import { AuthService, NATIVE_SCHEME } from 'src/app/services/auth/auth.service';
import { DeeplinkService } from '@app/services/deeplink/deeplink.service';
import { DialogService } from '@app/services/dialog/dialog.service';
import { ErrorService } from 'src/app/services/error/error.service';
import { Broadcaster } from 'src/app/services/events/broadcaster.class';
import { describePostAuthFailure, LoginService, PostAuthDestination, SessionRejectedError } from 'src/app/services/login/login.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { SanitizationService } from 'src/app/services/sanitization/sanitization.service';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { catchError, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Logger, LoggingService } from 'ionic-logging-service';
import { ActivatedRoute, Router } from '@angular/router';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { LogoutService } from '@app/services/logout/logout.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { CLIENT_ID_BROWSER, CLIENT_ID_NATIVE, SIGN_IN_QUERY_PARAMS } from '@app/services/auth/auth.constants';
import { PkceService } from '@app/services/pkce/pkce.service';
import { PostAuthNavigationService } from '@app/services/post-auth-navigation/post-auth-navigation.service';
import { TranslateService } from '@ngx-translate/core';

export const ALLOWED_DOMAINS = [
  'printix.net',
  'us.printix.net',
  'devenv.printix.net',
  'devenv.us.printix.net',
  'testenv.printix.net',
  'testenv.us.printix.net'
];

export const SANDBOX_DOMAIN = 'sandbox.printix.dev';

@Component({
    selector: 'app-login',
    templateUrl: './login.page.html',
    styleUrls: ['./login.page.scss'],
    standalone: false
})
export class LoginPage implements OnInit, OnDestroy {
  private static readonly AUTH_WAITING_DEBOUNCE_MS = 1000;

  public authWaiting = signal(false);
  public isLoading = signal(false);
  public queryParams: { [key: string]: string | undefined };
  public showTenantNameSpinner = signal(false);
  public  buildInAuthFormSubmitted: boolean = false;
  public  buildInAuthWaiting: boolean = false;
  public  buildInAuthUrl: string = null;
  public  errorMessage: string = null;
  public  errorStatus: number = undefined;
  public  isCordova: boolean = this.platformService.isCordova();
  public  isOnline = this.networkService.hasInternetAccess;
  private logger: Logger;
  public  loginFailed: string = null;
  public  resolvingHttpRequest: boolean = false;
  private authWaitingTimeout: ReturnType<typeof setTimeout> | null = null;
  private unSubscribe = new Subject<void>();
  private initUnSubscribe = new Subject<void>();
  public showTenantDoesNotExist = signal(false);
  public showTenantCouldNotBeLoaded = signal(false);
  public showTenantIsDeactivated = signal(false);

  public  userInputTenantDomain: string = null;
  public  validatedTenantLessDomain: string = null;
  public  validatedTenantDomain: string = null;
  public  userInputEnvironmentDomain: string = null;
  public  signInCode: string | null = null;
  public  showTenantDomainInput = false;
  // Native sign-out URL captured from the current environment before
  // resetEnvironmentEndpoints() wipes the endpoints back to prod defaults.
  private pendingSignOutUrl: string | null = null;

  constructor (
    private authService: AuthService,
    private broadcaster: Broadcaster,
    private environmentService: EnvironmentService,
    private errorService: ErrorService,
    private deeplinkService: DeeplinkService,
    private dialogService: DialogService,
    private inAppBrowser: InAppBrowser,
    private loginService: LoginService,
    private loggingService: LoggingService,
    private logoutService: LogoutService,
    private navController: NavController,
    private networkService: NetworkService,
    private pageTitleService: PageTitleService,
    private pkceService: PkceService,
    public platform: Platform,
    private platformService: PlatformService,
    private postAuthNavigationService: PostAuthNavigationService,
    private route: ActivatedRoute,
    private router: Router,
    private safariViewController: SafariViewController,
    private sanitizationService: SanitizationService,
    private tenantService: TenantService,
    private toastController: ToastController,
    private translateService: TranslateService
  ) {
    this.logger = this.loggingService.getLogger("[LoginPage]");
    const methodName = "ctor";
    this.logger.entry(methodName);
    this.errorService.stateError = false;
  }

  ngOnInit(): void {
    this.pageTitleService.setTitle('');
    this.logger.info('ngOnInit()');
    this.listenForPageReset();
    this.listenForEjectionReason();
    this.platform.ready().then(() => {
      if (this.isCordova) {
        // If we arrived here to sign out, build the native sign-out URL from the
        // CURRENT environment endpoints BEFORE resetEnvironmentEndpoints() resets
        // them to prod defaults — otherwise we'd hit the prod /logout on dev/test.
        this.route.queryParams.pipe(take(1)).subscribe(params => {
          this.pendingSignOutUrl = params['signOutParam'] === 'USER_SIGN_OUT'
            ? this.buildNativeSignOutUrl()
            : null;
          this.environmentService.resetEnvironmentEndpoints()
          .pipe(takeUntil(this.initUnSubscribe))
          .subscribe(() => {
            this.initPage();
          });
        });
      }
    });
  }

  /**
   * Keeps the ejection explanation in step with the URL for as long as the page lives.
   *
   * A subscription rather than a one-shot read in initPage(): an ejection often navigates to
   * /sign-in while the app is already there, and IonicRouteStrategy reuses the component for a
   * query-param-only change, so ngOnInit never runs again.
   */
  private listenForEjectionReason(): void {
    this.route.queryParams
      .pipe(takeUntil(this.initUnSubscribe))
      .subscribe((params) => {
        this.errorMessage = params['signOutParam'] === 'ACCESS_DENIED' ? 'ACCESS_DENIED' : null;
      });
  }

  private listenForPageReset(): void {
    this.broadcaster.on('RESET_LOGIN_PAGE')
    .pipe(takeUntil(this.initUnSubscribe))
    .subscribe(() => {
      this.logger.info('RESET_LOGIN_PAGE broadcast received');
      this.resetPageState();
    });
  }

  ngOnDestroy(): void {
    this.clearAuthWaitingTimeout();
    this.unSubscribe.next();
    this.unSubscribe.complete();
    this.initUnSubscribe.next();
    this.initUnSubscribe.complete();
  }

  private resetPageState(): void {
    this.logger.info('resetPageState()');
    // Reset loading and error states so the page is interactive
    this.resetLoadingStates();
    this.resetErrorStates();
    this.showTenantDomainInput = false;

    // Complete the previous Subject to tear down any active takeUntil subscriptions,
    // then recreate it so SafariViewController subscriptions can be properly managed
    // on each page visit
    this.unSubscribe.next();
    this.unSubscribe.complete();
    this.unSubscribe = new Subject<void>();
  }

  private resetLoadingStates(): void {
    this.clearAuthWaiting();
    this.isLoading.set(false);
    this.showTenantNameSpinner.set(false);
  }

  private resetErrorStates(): void {
    this.showTenantDoesNotExist.set(false);
    this.showTenantCouldNotBeLoaded.set(false);
    this.showTenantIsDeactivated.set(false);
  }

  private initPage(): void {
    this.logger.info('initPage()');
    this.resetLoadingStates();
    this.route.queryParams.pipe(take(1))
    .subscribe(params => {
      this.queryParams = params;
      // errorMessage is owned by listenForEjectionReason(), not set here: this read happens
      // once, and the ejection can arrive while the page is already open. It previously read
      // params['ACCESS_DENIED'] — a key nothing sends — and assigned 'AccessDenied', which is
      // not on the caption CDN, so it never fired and could not have rendered if it had.
      // Subscribe to the deeplink stream before triggering the sign-out redirect —
      // the auth server's /logout returns to the app via printixapp:/ and must be
      // caught here. The native registration itself lives in DeeplinkService.
      this.listenForRedirect();
      if (this.pendingSignOutUrl) {
        this.openSignOutUrl(this.pendingSignOutUrl);
        this.pendingSignOutUrl = null;
      }
    });
  }

  // Builds the native sign-out URL. Must be called while the user's environment
  // endpoints are still loaded (see ngOnInit).
  // NOTE: the redirect target is 'printixapp:/' (single slash) — the auth server
  // could not redirect to 'printixapp://' (two slashes). Revisit alongside the
  // deeplinks plugin config / APP_ENDPOINT if that needs to change.
  private buildNativeSignOutUrl(): string {
    const endpointSignOut: string = this.environmentService.getEnvironmentEndpoint['ENDPOINT_SIGN_OUT_NATIVE'];
    return `${endpointSignOut}?post_logout_redirect_uri=${encodeURIComponent('printixapp:/')}&client_id=${CLIENT_ID_NATIVE}`;
  }

  // Final step of the sign-out flow on native: open the sign-out URL in the same
  // browser that holds the SSO session cookie. The auth server clears the session
  // and redirects back via the printixapp:/ deeplink caught by listenForRedirect()
  // (no code present, so the user simply lands here, signed out).
  private openSignOutUrl(signOutUrl: string): void {
    const methodName = 'openSignOutUrl() ';
    this.logger.info(methodName + signOutUrl);
    this.safariViewController.isAvailable()
    .then((available: boolean) => {
      if (available) {
        this.safariViewController.show({ url: signOutUrl })
          .pipe(takeUntil(this.unSubscribe))
          .subscribe();
      } else {
        // fallback to inAppBrowser
        this.inAppBrowser.create(signOutUrl, '_system');
      }
      // The device browser is now the user's feedback — dismiss the sign-out
      // spinner shown by MenuComponent.handleSignOut() that covered the hand-off.
      this.dialogService.hideLoadingSpinnerDialog(methodName);
    });
  }

  // #################################### //
  // ### AUTHENTICATE AFTER SIGN-IN ##### //
  // #################################### //

  // Subscribes (once per page instance) to the app-lifetime deeplink stream owned by
  // DeeplinkService. Both the sign-in MATCH and the sign-out NO_MATCH arrive as plain
  // emissions on the same stream, so this no longer has to be torn down and re-armed
  // per sign-in — the single native registration lives in the service. takeUntil the
  // page-lifetime subject keeps it alive across resetPageState().
  public listenForRedirect() {
    const methodName = 'listenForRedirect() ';
    this.logger.info(methodName);
    this.deeplinkService.deeplink$.pipe(
      switchMap(event => {
        if (!event.matched) {
          // NO_MATCH is the normal outcome of a sign-out return: the auth server
          // redirects back to printixapp:/ with no code, so there is nothing to
          // exchange and we simply land on the signed-out LoginPage. Logged at debug
          // level so it doesn't read as an error during sign-out QA.
          this.logger.debug(methodName + 'deeplink: NO_MATCH (expected on sign-out return — no code to exchange)');
          if (this.isCordova) {this.safariViewController.hide();}
          this.resetLoadingStates();
          // Insurance: ensure the sign-out spinner is gone once we land back here.
          this.dialogService.hideLoadingSpinnerDialog(methodName + 'NO_MATCH');
          return of(null);
        }
        const match = event.match;
        this.logger.info(methodName + 'deeplink: MATCH - code.length' + match.$link.url.split("code=")[0].length);
        this.clearAuthWaiting();
        if (this.isCordova) {this.safariViewController.hide();}
        this.signInCode = match.$args && match.$args.code ?  match.$args.code : null;

        // Store idpLoginToken and wait for storage to complete before proceeding
        // This prevents a race condition on native where SQLite write may not finish
        // before getTenantListForUser() reads it on the tenant-select page
        if (match.$args && match.$args.idpLoginToken) {
          this.logger.info(methodName + 'deeplink - match.$args.idpLoginToken ' + match.$args.idpLoginToken.length);
          const idpLoginToken = match.$args['idpLoginToken'];
          return this.tenantService.setIdpLoginToken(idpLoginToken);
        }
        return of(null);
      }),
      takeUntil(this.initUnSubscribe)
    ).subscribe(() => {
      this.handleUserAuthentication();
    });
  }

  private handleUserAuthentication(): void {
    const methodName = 'handleUserAuthentication';
    if (this.signInCode) {
      // Show the page spinner the moment we return from the browser with a code,
      // so the token exchange + post-auth navigation are covered instead of briefly
      // flashing the sign-in form. authSuccessful() keeps isLoading set until the
      // page navigates away; every error exit below clears it.
      this.isLoading.set(true);
      this.authService.exchangeCodeForTokens(this.signInCode, this.validatedTenantDomain).pipe(take(1))
      .subscribe({
        next: () => {
          this.logger.info(methodName + ' - verified');
          this.resetLoadingStates();
          this.broadcaster.broadcast('ENABLE_RESUME');
          this.authSuccessful();
        },
        error: (error) => {
          this.logger.error(methodName + ' ERROR');
          // Non-HTTP errors (e.g. PKCE code_verifier missing) are precondition failures,
          // not server errors — surface them inline so the user can retry sign-in instead
          // of routing through the HTTP error modal.
          if (!(error instanceof HttpErrorResponse)) {
            this.resetLoadingStates();
            this.signInCode = null;
            this.errorMessage = 'ErrorLoginGeneral';
            return;
          }
          // Clear the spinner so the HTTP error modal isn't left sitting over it.
          this.resetLoadingStates();
          // A 400 invalid_grant during the code exchange is usually self-inflicted —
          // e.g. starting a second sign-in overwrote the first verifier, or completing a
          // stale browser session. Surface it as a lightweight toast and let the user
          // retry from the sign-in page, instead of the full HTTP error modal. Scoped to
          // 400 only: the 401 invalid_grant keeps its original handleHttpClientResponseError
          // path (incl. the auto sign-out). We can't distinguish a "naughty user"
          // invalid_grant from a genuine server one, so the message stays generic.
          if (error.status === 400 && error.error?.error === 'invalid_grant') {
            this.logger.info(methodName + ' - invalid_grant; prompting retry via toast');
            this.signInCode = null;
            // Composed from individual captions -> "Sign in Failed, Try again".
            const message = this.translateService.instant('SignIn')
              + ' ' + this.translateService.instant('Failed')
              + ', ' + this.translateService.instant('TryAgain');
            this.showErrorToast(message);
            return;
          }
          this.errorService.handleHttpClientResponseError(error, 'GET', methodName)
          .subscribe(result => {
            if (result === 'unauthenticated') {
              this.logger.info('User unauthenticated');
            }
          });
        }
      });
    }
  }

  // Lightweight, non-blocking error feedback (mirrors AuthPage.showErrorToast).
  // Used for self-recoverable sign-in failures where the full HTTP error modal would be
  // too heavy. Takes an already-translated message string and presents it.
  private async showErrorToast(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message,
      color: 'danger',
      duration: 4000,
      position: 'top'
    });
    await toast.present();
  }

  public authSuccessful(): void {
    this.logger.info('authSuccessful()');
    this.isLoading.set(true);
    this.broadcaster.broadcast('ENABLE_RESUME');
    let queryParams: { [key: string]: string } = {};
    this.route.queryParams.pipe(
      take(1),
      switchMap(params => {
        queryParams = params['registrationCode'] ? { registrationCode: params['registrationCode'] } : {};
        return this.loginService.resolvePostAuthNavigation$().pipe(
          tap((destination: PostAuthDestination) => this.navigatePostAuth(destination, queryParams))
        );
      }),
      catchError(error => {
        this.isLoading.set(false);
        this.logger.error('authSuccessful() - Error during post-auth navigation - '
          + describePostAuthFailure(error));
        // Same split as AuthPage: a rejected tenant list means the stored credentials are
        // dead, and sending that to /tenant-select only makes it re-issue the same failing
        // call — which routes through ErrorService into a sign-out. Anything else may
        // genuinely be an unresolved tenant, so it keeps the chooser.
        if (error instanceof SessionRejectedError) {
          this.handleRejectedSession();
        } else {
          this.navController.navigateRoot(['/tenant-select'], { queryParams });
        }
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Recovers this page after the backend rejects the tenant-list call.
   *
   * The token here is seconds old — authSuccessful() runs straight after
   * exchangeCodeForTokens() succeeded — so this is the backend refusing the list for a user who
   * has just authenticated, not a stale session. Signing in again will most likely fail the same
   * way; the sign-out is so the app does not hold credentials the backend will not honour.
   *
   * There is nothing to navigate to: this page *is* /sign-in, and onSameUrlNavigation defaults
   * to 'ignore', so the page has to put itself back in order. signInCode is cleared separately
   * from page state because it is a spent one-time code: left set, the guard in
   * handleUserAuthentication() would re-post it on the next deeplink emission.
   */
  private handleRejectedSession(): void {
    const methodName = 'handleRejectedSession() ';
    this.logger.info(methodName + 'tenant list rejected for a freshly authenticated user');
    // No spinner over the revoke, unlike AuthPage: this template makes it exclusive, so it
    // would replace the form — and nothing bounds the revoke, so a stalled request would need
    // an app restart. Leaving the form up keeps the page self-recoverable.
    this.logoutService.signOutAndRecover(() => this.recoverToSignInForm());
  }

  /** Returns the page to a usable sign-in form, explains why, and drops the spent code. */
  private recoverToSignInForm(): void {
    this.resetPageState();
    this.signInCode = null;
    // The user tapped sign-in, completed the whole IDP flow and exchanged their code
    // successfully — so unlike the cold-start bounce, this page is *not* its own explanation.
    // Landing back on the identical form with nothing said reads as the button having done
    // nothing, and the IDP session cookie survives, so tapping it again returns here just as
    // silently. Uses ACCESS_DENIED rather than the AccessDenied of initPage():183 — that
    // branch reads params['ACCESS_DENIED'] while the only navigation carrying it sends
    // { signOutParam: 'ACCESS_DENIED' }, so it never fires and is no evidence the caption
    // exists. ACCESS_DENIED is translated in live GO flows (go.page.ts), and is what AuthPage
    // now shows for the same outcome. Set after resetPageState(), which clears the states
    // around it but not errorMessage.
    this.errorMessage = 'ACCESS_DENIED';
  }

  private navigatePostAuth(destination: PostAuthDestination, queryParams: { [key: string]: string }): void {
    if (destination === 'app') {
      const registrationCode = queryParams['registrationCode'];
      if (registrationCode) {
        this.postAuthNavigationService.navigateToCardRegistration(registrationCode);
        return;
      }
      this.postAuthNavigationService.navigateIntoApp();
    } else if (destination === 'no-access') {
      // Terminal for this session, so drop the stash rather than leave it to fire on a
      // later sign-in that has nothing to do with the route that was interrupted.
      this.postAuthNavigationService.discardReturnUrl();
      this.navController.navigateRoot(['/auth'], { queryParams: { noAccess: '1' } });
    } else {
      this.navController.navigateRoot(['/tenant-select'], { queryParams });
    }
  }

  // #################################### //
  // ### SIGN IN FUNCTIONALITY ########## //
  // #################################### //

  /**
   * Sets authWaiting to true with an automatic debounce reset.
   * Prevents fast double-clicks from opening multiple external sign-in views,
   * while ensuring buttons become re-enabled if the user closes the external
   * sign-in screen without completing authentication.
   */
  private setAuthWaitingWithDebounce(): void {
    this.clearAuthWaitingTimeout();
    this.authWaiting.set(true);
    this.authWaitingTimeout = setTimeout(() => {
      this.logger.info('authWaiting debounce expired — re-enabling sign-in buttons');
      this.authWaiting.set(false);
      this.authWaitingTimeout = null;
      this.blurActiveElement();
    }, LoginPage.AUTH_WAITING_DEBOUNCE_MS);
  }

  /**
   * Clears authWaiting and cancels any pending debounce timeout.
   * Always use this instead of authWaiting.set(false) directly
   * to prevent stale timeouts from firing after auth completes.
   */
  private clearAuthWaiting(): void {
    this.clearAuthWaitingTimeout();
    this.authWaiting.set(false);
  }

  private clearAuthWaitingTimeout(): void {
    if (this.authWaitingTimeout !== null) {
      clearTimeout(this.authWaitingTimeout);
      this.authWaitingTimeout = null;
      this.blurActiveElement();
    }
  }

  private blurActiveElement(): void {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  public async goToSignInApp(signIn_Method: string): Promise<void> {
    const methodName = 'goToSignInApp() ';
    this.logger.info(methodName + '- signIn_Method: ' + signIn_Method);
    // The message explains the *previous* session ending, so drop it — and the parameter with
    // it. A later ejection navigates to this same URL, and onSameUrlNavigation defaults to
    // 'ignore', so leaving the parameter in place means that navigation is dropped, queryParams
    // never re-emits, and the message can never come back. void: nothing depends on the
    // navigation finishing, and a floating promise here would be an unhandled rejection.
    this.errorMessage = null;
    void this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
    this.broadcaster.broadcast('DISABLE_RESUME');
    this.setAuthWaitingWithDebounce();
    const signInApp_Url = await this.buildSignInUrl(signIn_Method);

    this.safariViewController.isAvailable()
    .then((available: boolean) => {
      if (available) {
        this.safariViewController.show({
          url: signInApp_Url.toString()
        })
        .pipe(takeUntil(this.unSubscribe))
        .subscribe((response) => {
          this.logger.info('nativeSignIn: safariViewController - SignInApp response: ' + response.event);
          // If user closes Safari without completing OAuth, re-enable resume
          if (response.event === 'exit' || response.event === 'closed') {
            this.logger.info(methodName + '- user closed Safari, re-enabling resume');
            this.broadcaster.broadcast('ENABLE_RESUME');
          }
          this.clearAuthWaiting();
        }, (error) => {
          this.logger.error(methodName + '- SafariViewController error: ' + JSON.stringify(error));
          this.broadcaster.broadcast('ENABLE_RESUME');
          this.clearAuthWaiting();
        });
      } else {
        // fallback to inAppBrowser
        this.logger.info('nativeSignIn:safariViewController - not available, using inAppBrowser to open device browser');
        this.inAppBrowser.create(signInApp_Url.toString(), '_system');
        this.broadcaster.broadcast('ENABLE_RESUME');
      }
    });
  }

  // signInApp_Url example:
  // https://auth.printix.net/oauth/authorize/tenant/abc.printix.net
  // ?response_type=code
  // &client_id=web_app_user_app
  // &redirect_uri=printixapp://?abc.printix.net
  private async buildSignInUrl(signIn_Method: string): Promise<URL> {
    // Microsoft/Google IDP sign-in returns a `code` on the printixapp:/ deeplink that
    // exchangeCodeForTokens() exchanges — and that exchange now hard-requires a PKCE
    // verifier. So these branches must generate + store the challenge and bind the
    // issued code to it, exactly like the tenant paths below; otherwise the exchange
    // bails with "missing PKCE code_verifier". (Requires the IDP-signin endpoint to
    // honour code_challenge server-side.)
    if (signIn_Method === 'id_ms' || signIn_Method === 'id_google') {
      const endpoint = signIn_Method === 'id_ms'
        ? this.environmentService.getEnvironmentEndpoint['ENDPOINT_SIGN_IN_MS']
        : this.environmentService.getEnvironmentEndpoint['ENDPOINT_SIGN_IN_GOOGLE'];
      const idpSignInUrl = new URL(`${endpoint}${NATIVE_SCHEME}`);
      // Bind the issued code to the SAME client_id that exchangeCodeForTokens() sends.
      // The endpoint URL already carries redirect_uri. Without this the IDP authorize
      // request omits the client, so the server binds the code to a different/default
      // client and the token exchange (which always sends native_app_user_app) is
      // rejected with invalid_grant.
      // NOTE: deliberately NOT sending response_type here. /identity-providers/.../signin
      // is an IDP-initiated entry point (not /oauth/authorize); adding response_type=code
      // made it loop (ERR_TOO_MANY_REDIRECTS), so we only add client_id.
      // These IDP buttons are native-only (web signs in via a separate portal), so the
      // client is always CLIENT_ID_NATIVE — no isCordova branch needed.
      idpSignInUrl.searchParams.set('client_id', CLIENT_ID_NATIVE);
      const codeChallenge = await this.pkceService.generatePkceChallenge();
      idpSignInUrl.searchParams.set('code_challenge', codeChallenge);
      idpSignInUrl.searchParams.set('code_challenge_method', 'S256');
      return idpSignInUrl;
    }
    
    const ENDPOINT_AUTHORIZE = signIn_Method === 'tenant_less' ?
      this.environmentService.getEnvironmentEndpoint['ENDPOINT_TENANT_LESS'] :
      // 'https://auth.printix.net/oauth/authorize'
      this.environmentService.getEnvironmentEndpoint['ENDPOINT_TENANT_DOMAIN'];
      // 'https://auth.printix.net/oauth/authorize/tenant/'
    
    const signInApp_Url = signIn_Method === 'tenant_less' ?
      new URL(ENDPOINT_AUTHORIZE) :
      // 'https://auth.printix.net/oauth/authorize'
      new URL(`${ENDPOINT_AUTHORIZE}${this.validatedTenantDomain}`);
      // 'https://auth.printix.net/oauth/authorize/tenant/' 'abc.printix.net'

    const redirectUrl = signIn_Method === 'tenant_less' ?
      new URL(NATIVE_SCHEME) :
      // 'printixapp://'
      new URL(`${NATIVE_SCHEME}?${this.validatedTenantDomain}`);
      // 'printixapp://?abc.printix.net'

    if (SIGN_IN_QUERY_PARAMS && Array.isArray(SIGN_IN_QUERY_PARAMS)) {
      SIGN_IN_QUERY_PARAMS.forEach(param => {
        const key = Object.keys(param)[0];
        let value = param[key];
        if (key === 'client_id') {
            value = this.isCordova ? CLIENT_ID_NATIVE : CLIENT_ID_BROWSER;
        }
        if (key === 'redirect_uri') {
            value = redirectUrl.toString();
        }
        signInApp_Url.searchParams.set(key, value);
      });
    }

    // Add PKCE parameters for enhanced security sign-in flow
    const codeChallenge = await this.pkceService.generatePkceChallenge();
    signInApp_Url.searchParams.set('code_challenge', codeChallenge);
    signInApp_Url.searchParams.set('code_challenge_method', 'S256');
    // Force the IDP to show credentials UI even when an SVC/CCT session cookie
    // survives our local sign-out — otherwise re-sign-in is silent on native.
    signInApp_Url.searchParams.set('prompt', 'login');
    return signInApp_Url;
  }

  // #################################### //
  // ### USER INPUT TENANT/DOMAIN ####### //
  // #################################### //

  public showInputForTenantDomain(): void {
    this.logger.info('(click) showInputForTenantDomain()');
    this.showTenantDomainInput = true;
  }

  public validateUserDomainInput(): void {
    this.logger.info('(click) validateUserDomainInput()');
    this.resetErrorStates();

    if (!this.userInputTenantDomain) {
      this.logger.warn('Empty tenant domain input');
      return;
    }

    // Step 1: Check if this is a tenantless domain (no tenant name)
    this.validatedTenantLessDomain = this.sanitizationService.checkForTenantlessDomain(this.userInputTenantDomain); 
    if (this.validatedTenantLessDomain) {
      this.setTenantLessDomain();
      return;
    }

    // Step 2: Not tenantless, so validate as tenant+domain (e.g., abc.printix.net)
    this.validatedTenantDomain = this.sanitizationService.validateTenantDomain(this.userInputTenantDomain);
    if (!this.validatedTenantDomain) {
      this.logger.error('Invalid tenant domain format');
      this.showTenantDoesNotExist.set(true);
      return;
    }

    this.checkIfTenantExists();
  }

  private setTenantLessDomain() {
    this.logger.info('Tenantless domain detected');
    this.tenantService.setTenantInputValues(null, this.validatedTenantLessDomain);
    this.showTenantNameSpinner.set(true);
    this.environmentService.setEnvironmentVariablesFromUrl(this.validatedTenantLessDomain).pipe(take(1))
    .subscribe({
      next: () => {
        this.showTenantNameSpinner.set(false);
        this.userInputTenantDomain = null;
        this.goToSignInApp('tenant_less');
      },
      error: (error) => {
        this.logger.error('Error setting environment variables for tenantless domain:', error);
        this.showTenantNameSpinner.set(false);
      }
    });
  }  

  public checkIfTenantExists(): void {
    this.logger.info('checkIfTenantExists()');
    this.showTenantNameSpinner.set(true);
    // Set environment variables and check if tenant exists
    this.environmentService.setEnvironmentVariablesFromUrl(this.validatedTenantDomain).pipe(
      take(1),
      switchMap(() => {
        return this.tenantService.checkIfUserTenantExists(this.validatedTenantDomain).pipe(
          tap(() => {
            this.logger.info('Tenant exists -> redirectToSignInApp()');
            this.showTenantNameSpinner.set(false);
            this.userInputTenantDomain = null;
            this.tenantService.setTenantInputValues(this.validatedTenantDomain, null);
            this.goToSignInApp('tenant_domain');
          }),
          catchError((error) => {
            this.logger.error('## ERROR TENANT_OR_DOMAIN DOES_NOT_EXIST');
            this.setErrorState(error);
            return EMPTY;
          })
        );
      })
    ).subscribe();
  }

  // #################################### //
  // ### STATES ######################### //
  // #################################### //
  
  private setErrorState(error): void {
    this.environmentService.resetEnvironmentEndpoints().subscribe(() => {
      this.resetLoadingStates();
      this.resetErrorStates();
      if (error.status === 404) {
        this.showTenantDoesNotExist.set(true);
      } else if (error.status === 410) {
        this.showTenantIsDeactivated.set(true);
      } else if ((error.status === 503 || error.status === 0) && this.validatedTenantDomain.includes('.printix.dev')) {
        this.logger.info('checkIfUserTenantDomainExists() httpErrorResponse: 503 - Tenant could not be loaded');
        this.errorStatus = error.status;
        this.showTenantCouldNotBeLoaded.set(true);
      } else {
        this.errorService.handleHttpClientResponseError(error, 'GET', 'checkIfUserTenantExists()')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.error('User unauthenticated');
          }
        });
      }
    });
  }

  public hideErrorText(event) {
    this.resetErrorStates();
  }

  public clearTenantInput(): void {
    this.userInputTenantDomain = null;
    this.validatedTenantDomain = null;
    this.resetErrorStates();
  }

  public goBackToMainSignIn(): void {
    this.errorMessage = null;
    this.validatedTenantDomain = null;
    this.showTenantDomainInput = false;
    this.resetErrorStates();
  }
}
